// Sleuth investigations after a player rug (spec 10.1). Scales with heat.
// Exposed: followers -20-50%, trust -30, 7-day launchpad ban, and a public thread on Chirp.

import { TUNABLES } from "@degen/shared/config/tunables";
import type { Rng } from "../rng";
import { dayOf, pushLog, type GameState } from "../state";
import { addTrust } from "../player";

const L = TUNABLES.launch;

export interface Investigation {
  tokenId: string;
  resolveTick: number;
  chance: number;
}

export interface ChoiceOption {
  id: string;
  label: string;
}

/** A popup that needs a decision (spec 10.1). The UI pauses the clock while any are pending. */
export interface ChoiceEvent {
  id: string;
  kind: "phishing" | "fake_claim" | "collab" | "tutorial_dm";
  tick: number;
  title: string;
  from: string;
  body: string;
  options: ChoiceOption[];
  tokenId?: string;
  payUsd?: number;
}

export interface CollabDeal {
  tokenId: string;
  untilTick: number;
}

export interface EventsState {
  investigations: Investigation[];
  pending: ChoiceEvent[];
  nextId: number;
  collabs: CollabDeal[];
  asleep: boolean; // no choice popups while the player sleeps
}

export function createEvents(): EventsState {
  return { investigations: [], pending: [], nextId: 1, collabs: [], asleep: false };
}

export function scheduleInvestigation(state: GameState, rng: Rng, tokenId: string) {
  const chance = Math.min(0.95, L.sleuthChanceBase + state.player.heat / 200);
  state.events.investigations.push({ tokenId, chance, resolveTick: state.tick + rng.int(L.sleuthDelayTicks[0], L.sleuthDelayTicks[1]) });
}

export function eventsTick(state: GameState, rng: Rng) {
  const ev = state.events;
  if (!ev.investigations.length || ev.investigations[0].resolveTick > state.tick) return;
  const due = ev.investigations.filter((i) => i.resolveTick <= state.tick);
  ev.investigations = ev.investigations.filter((i) => i.resolveTick > state.tick);
  for (const inv of due) {
    const t = state.market.tokens[inv.tokenId];
    const ticker = t ? `$${t.ticker}` : "your coin";
    if (!rng.chance(inv.chance)) {
      pushLog(state, "sleuth_cleared", `The sleuths lost the trail on ${ticker}. You got away with it... this time.`, { tokenId: inv.tokenId });
      continue;
    }
    const p = state.player;
    const lost = Math.round(p.followers * rng.range(L.exposedFollowersPct[0], L.exposedFollowersPct[1]));
    p.followers -= lost;
    addTrust(p, -L.exposedTrust);
    p.flags.launchpadBanUntilDay = dayOf(state.tick) + L.banDays;
    pushLog(state, "exposed", `🚨 EXPOSED. A sleuth traced the ${ticker} rug to @${p.handle}. -${lost.toLocaleString()} followers, launchpad ban ${L.banDays} days.`, {
      tokenId: inv.tokenId,
      lost,
    });
  }
}
